import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import { media } from 'src/styles/styled-components'
import { BlogAuthorModel } from 'src/models/index.js'
import routesConfig from 'src/routes.config'
import { deviceInfoProvider } from 'src/utils'

// ArComponents
import ArBaseContainer from 'src/components/ArBaseContainer/ArBaseContainer'
import ArBaseImage from 'src/components/ArBaseImage/ArBaseImage'
import ArMonoTag from 'src/components/typography/ArMonoTag'
import ArBlogAuthor from 'src/components/blog/ArBlogAuthor/ArBlogAuthor'

const StyledWrapper = styled.section`
  background-color: ${ p => p.theme.colors.backgroundDarkSections };
  color: ${ p => p.theme.colors.white };
  padding-bottom: 30px;
  padding-top: 20px;

  ${ media.tablet`
    padding-top: 30px;
    padding-bottom: 40px;
  ` }

  ${ media.desktop`
    padding-top: 60px;
    padding-bottom: 80px;
  ` }
`

const StyledHeading = styled.h1`
  color: ${ p => p.theme.colors.white };
  font-size: 2rem;
  font-weight: ${ p => p.theme.fontWeight.primary.Book };
  letter-spacing: .01rem;
  line-height: 1.4;
  margin-bottom: 20px;
  ${ media.tablet`
    font-size: 3.8rem;
    line-height: 1.18;
    font-weight: ${ p => p.theme.fontWeight.primary.Light };
    margin-bottom: 30px;
  ` }
  ${ media.desktop`
    font-size: 4.5rem;
    line-height: 1.4;
    margin-bottom: 60px;
  ` }
`

const StyledList = styled.ul`
  ${ media.tablet`
    display: flex;
    justify-content: space-between;
  ` }

  > li {
    ${ media.tablet`
      width: 47.1%;
    ` }

    ${ media.desktop`
      width: 29.28%;
    ` }
  }

  li + li {
    margin-top: 45px;

    ${ media.tablet`
      margin-top: 0;
    ` }
  }
`

const StyledArticle = styled.article`
  ${ media.desktop`
    display: flex;
    flex-direction: column;
  ` }
`

const StyledArticleImage = styled(ArBaseImage)`
  border-radius: 4px;
  margin-bottom: 20px;
  overflow: hidden;

  ${ media.desktop`
    order: -3;
  ` }
`

const StyledArticleTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Bold };
  letter-spacing: 0.01em;
  line-height: 1.55;
  margin-bottom: 10px;

  ${ media.tablet`
    margin-bottom: 20px;
  ` }

  ${ media.desktop`
    margin-bottom: 13px;
    order: -2;
    font-size: 2.6rem;
    line-height: normal;
    font-weight: ${ ({ theme }) => theme.fontWeight.primary.Demi };
  ` }
`

const StyledArticleDescription = styled.p`
  font-size: 1.8rem;
  font-weight: ${ ({ theme }) => theme.fontWeight.primary.Light };
  letter-spacing: 0.01em;
  line-height: 1.55;
`

const StyledArticleMonoLabel = styled.div`
  display: flex;
  margin-top: 10px;

  ${ media.tablet`
    margin-top: 20px;
  ` }

  ${ media.desktop`
    margin-top: 0;
    margin-bottom: 23px;
    order: -1;
  ` }

  > ${ ArMonoTag } {
    margin-right: 10px;
    padding-right: 10px;
    position: relative;

    &:before {
      background-color: ${ p => p.theme.colors.tundora };
      content: '';
      height: 25px;
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
    }
  }
`

class ArFeaturedArticles extends React.Component {
  static propTypes = {
    articles: PropTypes.arrayOf(PropTypes.shape({
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
      title: PropTypes.string,
      description: PropTypes.string,
      author: BlogAuthorModel,
      category: PropTypes.string,
    })),
  }

  get articles () {
    if (this.props.device.isDesktop) return this.props.articles || []
    else if (this.props.device.isMobile || this.props.device.isTablet) return this.props.articles.slice(0, 2) || []
    else return []
  }

  get imgAspectRatio () {
    if (this.props.device.isDesktop) return 0.7
    else if (this.props.device.isTablet) return 0.7
    else if (this.props.device.isMobile) return 0.4
  }

  render () {
    return (
      <StyledWrapper>
        <ArBaseContainer>
          <StyledHeading>Related Articles</StyledHeading>
          <StyledList>
            {this.articles.map(({ image, title, description, category, author, slug }, index) =>
              <li key={index}>
                <Link to={routesConfig.company.blog({ slug: slug })}>
                  <StyledArticle>
                    <StyledArticleImage src={image.url} alt={image.alt} scAspectRatio={this.imgAspectRatio} scFit={'cover'} />
                    <StyledArticleTitle>{ title }</StyledArticleTitle>
                    <StyledArticleDescription>{ description }</StyledArticleDescription>
                    <StyledArticleMonoLabel>
                      <ArMonoTag>{ category }</ArMonoTag>
                      <ArBlogAuthor small author={author} />
                    </StyledArticleMonoLabel>
                  </StyledArticle>
                </Link>
              </li>
            )}
          </StyledList>
        </ArBaseContainer>
      </StyledWrapper>
    )
  }
}

export default deviceInfoProvider(ArFeaturedArticles)
